import React, { useState, useEffect, useRef } from 'react';
import { BsCamera, BsX } from 'react-icons/bs';

export default function AnamorphicParallax() {
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const [cameraActive, setCameraActive] = useState(false);
  const [facePos, setFacePos] = useState({ x: 0.5, y: 0.5 });
  const [smoothPos, setSmoothPos] = useState({ x: 0.5, y: 0.5 });
  const [faceDetected, setFaceDetected] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const smoothingRef = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!cameraActive) {
        setMousePos({
          x: e.clientX / window.innerWidth,
          y: e.clientY / window.innerHeight
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [cameraActive]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraActive(true);
        };
      }
    } catch (err) {
      console.error('Błąd dostępu do kamery:', err);
      alert('Nie udało się uzyskać dostępu do kamery. Upewnij się, że przeglądarka ma uprawnienia.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setFaceDetected(false);
    smoothingRef.current = { x: 0.5, y: 0.5 };
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const detectFace = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    let frameCount = 0;

    const detect = () => {
      if (!cameraActive) return;

      frameCount++;

      // co 2 klatkę (lepsza wydajność)
      if (frameCount % 2 !== 0) {
        animationRef.current = requestAnimationFrame(detect);
        return;
      }

      if (video.readyState !== video.HAVE_ENOUGH_DATA) {
        animationRef.current = requestAnimationFrame(detect);
        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      if (!canvas.width || !canvas.height) {
        animationRef.current = requestAnimationFrame(detect);
        return;
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      try {
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

        let totalX = 0;
        let totalY = 0;
        let pixelCount = 0;

        const step = 4;
        const width = canvas.width;
        const height = canvas.height;

        const startY = Math.floor(height * 0.15);
        const endY = Math.floor(height * 0.85);
        const startX = Math.floor(width * 0.25);
        const endX = Math.floor(width * 0.75);

        for (let y = startY; y < endY; y += step) {
          for (let x = startX; x < endX; x += step) {
            const i = (y * width + x) * 4;
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];

            const sum = r + g + b;
            if (sum < 120) continue;

            const nr = r / sum;
            const ng = g / sum;

            // STABILNA detekcja skóry
            const isSkin =
              nr > 0.36 && nr < 0.465 &&
              ng > 0.28 && ng < 0.363;

            if (isSkin) {
              totalX += x;
              totalY += y;
              pixelCount++;
            }
          }
        }

        // adaptacyjny próg
        const minPixels =
          ((endX - startX) * (endY - startY)) /
          (step * step) *
          0.002;

        if (pixelCount > minPixels) {
          const rawX = (totalX / pixelCount) / width;
          const rawY = (totalY / pixelCount) / height;

          // JEŚLI video jest mirrorowane CSS-em → ZOSTAW
          const newX = 1 - rawX;
          const newY = rawY;

          const mappedX = Math.max(0, Math.min(1, newX));
          const mappedY = Math.max(0, Math.min(1, newY));

          const dx = mappedX - smoothingRef.current.x;
          const dy = mappedY - smoothingRef.current.y;
          const dist2 = dx * dx + dy * dy;

          // mały próg, żeby zignorować mikroruchy / szum przy nieruchomej twarzy
          const jitterThreshold = 0.0001; // ~1% szerokości / wysokości
          const smoothing = 0.15; // mocniejsze wygładzanie (mniej drgań)

          if (dist2 > jitterThreshold) {
            smoothingRef.current.x += dx * smoothing;
            smoothingRef.current.y += dy * smoothing;
          }

          setFacePos({ ...smoothingRef.current });
          setSmoothPos({ ...smoothingRef.current });
          setFaceDetected(true);
        } else {
          setFaceDetected(false);
        }
      } catch (err) {
        console.error("Face detect error:", err);
      }

      animationRef.current = requestAnimationFrame(detect);
    };

    detect();
  };

  useEffect(() => {
    if (cameraActive) {
      detectFace();
    }
  }, [cameraActive]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const pos = cameraActive ? smoothPos : mousePos;
  
  const offsetX = (pos.x - 0.5) * 460; // trochę większy zakres ruchu
  const offsetY = (pos.y - 0.5) * 460;
  const rotateX = (pos.y - 0.5) * 165;
  const rotateY = (pos.x - 0.5) * -165;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 overflow-hidden">
      <video 
        ref={videoRef} 
        autoPlay 
        playsInline 
        muted
        className="fixed top-4 left-4 w-48 h-36 object-cover rounded-lg border-2 border-white/30 z-50"
        style={{ display: cameraActive ? 'block' : 'none' }}
      />
      <canvas ref={canvasRef} className="hidden" />
      
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={cameraActive ? stopCamera : startCamera}
          className={`${
            cameraActive 
              ? 'bg-red-500/80 hover:bg-red-600/80' 
              : 'bg-white/10 hover:bg-white/20'
          } backdrop-blur-md text-white px-6 py-3 rounded-full flex items-center gap-2 transition-all`}
        >
          {cameraActive ? <BsX size={24} /> : <BsCamera size={20} />}
          {cameraActive ? 'Zatrzymaj' : 'Włącz kamerę'}
        </button>
      </div>

      <div className="fixed top-4 left-4 z-40 bg-black/50 backdrop-blur-md text-white px-4 py-2 rounded-lg text-sm" style={{ marginTop: cameraActive ? '160px' : '0' }}>
        {cameraActive ? (
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${faceDetected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
            {faceDetected ? '✅ Twarz wykryta' : '❌ Szukam twarzy...'}
          </div>
        ) : (
          '🖱️ Ruch myszy'
        )}
        <div className="text-xs mt-1 text-white/60">
          X: {pos.x.toFixed(3)} Y: {pos.y.toFixed(3)}
        </div>
        {cameraActive && faceDetected && (
          <div className="text-xs mt-1 text-green-400">
            Offset: X={offsetX.toFixed(0)} Y={offsetY.toFixed(0)}
          </div>
        )}
      </div>

      <div 
        className="relative w-full h-screen flex items-center justify-center"
        style={{
          perspective: '1500px'
        }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            transform: `translate(${offsetX * 0.15}px, ${offsetY * 0.15}px)`
          }}
        >
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="absolute w-3 h-3 bg-purple-400 rounded-full"
              style={{
                left: `${(i * 7) % 100}%`,
                top: `${(i * 13) % 100}%`,
                transform: `translate(${offsetX * (i * 0.08)}px, ${offsetY * (i * 0.08)}px)`
              }}
            />
          ))}
        </div>

        <div
          className="relative z-10"
          style={{
            transform: `
              rotateX(${rotateX}deg) 
              rotateY(${rotateY}deg)
              translateZ(100px)
            `,
            transformStyle: 'preserve-3d'
          }}
        >
          <h1 
            className="text-9xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 mb-8 text-center"
            style={{
              transform: `translateZ(${offsetX * 4}px)`,
              textShadow: '0 0 40px rgba(168, 85, 247, 0.4)'
            }}
          >
            ANAMORFOZY
          </h1>

          <div className="grid grid-cols-3 gap-8 mt-16">
            {[...Array(9)].map((_, i) => {
              const row = Math.floor(i / 3);
              const col = i % 3;
              const depth = (row - 1) * 50 + (col - 1) * 30;
              
              return (
                <div
                  key={i}
                  className="w-32 h-32 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg shadow-2xl"
                  style={{
                    transform: `
                      translateZ(${depth + offsetX * 0.8}px)
                      rotateY(${rotateY + (col - 1) * 10}deg)
                      rotateX(${rotateX + (row - 1) * 10}deg)
                      scale(${1 + Math.abs(offsetX) * 0.004})
                    `,
                    transformStyle: 'preserve-3d',
                    opacity: 0.6 + (Math.abs(offsetX) * 0.004)
                  }}
                >
                  <div 
                    className="w-full h-full flex items-center justify-center text-white text-4xl font-bold"
                    style={{
                      transform: `translateZ(20px)`,
                    }}
                  >
                    {i + 1}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="absolute bottom-20 left-1/2 -translate-x-1/2"
          style={{
            transform: `
              translateX(-50%) 
              translateZ(${offsetY * 5}px)
              rotateX(${rotateX * 0.6}deg)
            `,
            transformStyle: 'preserve-3d'
          }}
        >
          <div className="text-center text-white/80 text-lg">
            <p className="mb-2">Porusz myszą lub włącz kamerę</p>
            <p className="text-sm text-white/60">aby zobaczyć efekt anamorfozy i paralaksy</p>
          </div>
        </div>

        <div
          className="absolute w-96 h-96 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-full blur-3xl"
          style={{
            top: '50%',
            left: '50%',
            transform: `
              translate(-50%, -50%)
              translate(${offsetX * 3}px, ${offsetY * 3}px)
            `
          }}
        />
      </div>

      <section className="relative z-10 min-h-screen flex items-center justify-center p-8 bg-black/30">
        <div
          style={{
            transform: `
              translateX(${offsetX * 0.9}px)
              translateY(${offsetY * 0.9}px)
              perspective(1500px)
            `
          }}
        >
          <div className="max-w-4xl mx-auto">
            <h2 
              className="text-6xl font-bold text-white mb-8"
              style={{
                transform: `rotateY(${rotateY * 0.5}deg) translateZ(50px)`,
                transformStyle: 'preserve-3d'
              }}
            >
              Sekcja z Paralaksą
            </h2>
            
            <div className="grid grid-cols-2 gap-8">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white/10 backdrop-blur-md p-6 rounded-xl"
                  style={{
                    transform: `
                      translateZ(${i * 20}px)
                      rotateY(${rotateY * 0.4 * (i % 2 ? 1 : -1)}deg)
                      translateX(${offsetX * 0.5 * (i % 2 ? 1 : -1)}px)
                    `,
                    transformStyle: 'preserve-3d'
                  }}
                >
                  <h3 className="text-2xl font-bold text-purple-300 mb-4">
                    Element {i + 1}
                  </h3>
                  <p className="text-white/80">
                    Ten element reaguje na ruch i zmienia swoją perspektywę w zależności od kąta patrzenia.
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}